import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import styles from './RetailValidationTable.module.scss'
import useValidationDraft from '@/Hooks/useValidationDraft'
import useRetailQuota from '@/Hooks/useRetailQuota'
import useRetailCollections from '@/Hooks/useRetailCollections'
import useSales from '@/Hooks/useSales'
import { AppWriteContext } from '@/Contexts/AppWriteContext'
import useTarifs from '@/Hooks/useTarifs'
import useStock, { type StockResult } from '@/Hooks/useStock'
import useTranslation from '@/Hooks/useTranslation'
import useHelpers from '@/Hooks/useHelpers'
import { parseSkuQtyInput } from '@/libs/skuQtyInput'
import { getStoredDiscount } from '@/libs/retail_rules'
import { getRetailStockDepots } from '@/libs/retailStockDepots'
import { Checkbox, States as CheckboxStates } from '@/components/Checkbox'
import Button from '@/components/Button'
import Confirm from '@/components/Confirm'
import ReliquatConfirm, { type ReliquatPopupLine } from './ReliquatConfirm'
import DiscountEditor from '@/components/DiscountEditor'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faXmark, faTrash, faArrowRotateLeft, faPlus, faTriangleExclamation, faBan, faArrowLeft, faCaretUp, faCaretDown, faCircleQuestion, faPercent } from '@fortawesome/free-solid-svg-icons'
import { faLoader, faStars } from '@fortawesome/pro-solid-svg-icons'
import { toast } from 'react-toastify'
import type { Order, OrderLine, SkuQuotaDetail } from '@/types'
import glob_const from '@/libs/glob_const'
import usePrefStore from '@/stores/prefs'
import useRetailRoleForCustomer from '@/Hooks/useRetailRoleForCustomer'

interface RetailValidationTableProps {
   order: Order
   validateMode: 'n1' | 'n2'
   /** 'draft' = brouillon manager new_mgr (N1/admin, messika-pwa#25) : pas de notion de demande client */
   mode?: 'validation' | 'draft'
   onValidated: () => void
   onClose: () => void
   /** Mode draft : validation finale / renvoi au client, via les flux partagés d'OrderDetail */
   onFinalValidation?: () => void
   onReturnToClient?: () => void
}

export interface RetailValidationTableHandle {
   /** Ajoute au brouillon une saisie `SKU[*|X]qté, …` ; true si au moins une ligne a été ajoutée */
   addFromInput: (text: string) => boolean
}

const RetailValidationTable = forwardRef<RetailValidationTableHandle, RetailValidationTableProps>(function RetailValidationTable({ order, validateMode, mode = 'validation', onValidated, onClose, onFinalValidation, onReturnToClient }, ref) {
   const isDraft = mode === 'draft'
   const { trans } = useTranslation()
   const { formatNumber, formatNumberWithNC, formatCESPrice, formatRetailPrice } = useHelpers()
   const { getRetailModelById, isExcluBoutique } = useRetailCollections()
   const { updateOrderLocally, deleteOrder, order_status, getCustomerById, getEffectiveRetailRole, hasN1Validator, type_order, validateRetailN2, validateRetailN1, computeRefusedLines, isOrderComplete } = useSales()
   const { currentUserId, awPushOrder } = { ...AppWriteContext() } as any
   const { getRetailQuota, getSkuRemaining } = useRetailQuota()
   const { getMinMaxCessPriceForModel, findRegionFromCessionTarifNames, findRegionFromPublicTarifNames } = useTarifs()
   const { fetchStock } = useStock()
   const retailRole = usePrefStore(state => state.retailRole)
   const isRetailManager = usePrefStore(state => state.isRetailManager)
   const setCurOrder = usePrefStore(state => state.setCurOrder)

   // Client + région/devise pour le calcul du PU (le valideur n'a pas forcément ce client comme cur_customer)
   const customer = useMemo(() => getCustomerById(order.customer_id), [getCustomerById, order.customer_id])

   // Rôle vis-à-vis du client de cette commande (pas le rôle global) :
   // PCB et stock dépendent d'admin / N1 sur CE client précisément, et le
   // fallback de getEffectiveRetailRole s'appuie aussi sur cette matrice.
   const roleForCustomer = useRetailRoleForCustomer(order.customer_id)
   const canShowPCB = roleForCustomer.isAdmin || roleForCustomer.isN1
   // Rôle effectif sur cet order précis (admin > N1 > N2 > client > null) — peut différer du retailRole global
   // si le user est par ex. N2 d'un client X mais N1 d'un autre client Y.
   const effectiveRetailRole = useMemo(() => getEffectiveRetailRole(order, currentUserId, retailRole, roleForCustomer, isRetailManager), [order, currentUserId, retailRole, roleForCustomer, isRetailManager, getEffectiveRetailRole])
   const orderHasN1 = useMemo(() => hasN1Validator(order, customer), [order, customer, hasN1Validator])
   // "Dernier maillon" : la validation va directement déclencher la synchro M3.
   //  - admin (shortcut quel que soit le statut)
   //  - N1 (sur wait_n1)
   //  - N2 mais pas de N1 derrière (validation directe)
   const isLastValidationStep = effectiveRetailRole === 'admin'
      || effectiveRetailRole === 'N1'
      || (effectiveRetailRole === 'N2' && !orderHasN1)
   const currency = customer?.tarifs.ces.currency ?? null   // CESSION : lookup tarif cession (resolvePU) ET devise d'affichage des prix retail (tout est en cession)
   const region = useMemo(() => {
      if (!customer) return null
      return findRegionFromCessionTarifNames(customer.tarifs.ces.tarif, customer.tarifs.ces.currency)
         ?? findRegionFromPublicTarifNames(customer.tarifs.ces.tarif, customer.tarifs.ces.currency)
   }, [customer, findRegionFromCessionTarifNames, findRegionFromPublicTarifNames])

   // État du brouillon transactionnel
   // - baseLines (1er arg) = snapshot initial du client, sert de référence pour reset()/isDirty
   // - order.lines (2e arg) = état persisté courant, pour reprendre une session de validation en cours
   // Brouillon manager : pas de demande client de référence → l'état courant fait foi
   const baseLines = isDraft ? order.lines : (order.original_lines ?? order.lines)
   const { draftLines, isDirty: isLinesDirty, setLineQty, setLineDiscount, deleteLine, undeleteLine, addLine, reset, buildFinalLines } = useValidationDraft(baseLines, order.lines)

   // Brouillon adresse (messika-pwa#5) — même principe que les lignes : `order.original_addresses`
   // est le snapshot de référence, `order.addresses` est l'état courant (déjà en Dexie via
   // updateOrderLocally). `order` est une live query Dexie : pas besoin d'état local, la prop
   // se met à jour toute seule après l'écriture.
   const baseAddresses = isDraft ? order.addresses : (order.original_addresses ?? order.addresses)
   const isAddressDirty = useMemo(
      () => JSON.stringify(order.addresses) !== JSON.stringify(baseAddresses),
      [order.addresses, baseAddresses]
   )
   const isDirty = isLinesDirty || isAddressDirty

   // Remise éditable uniquement sur ECF (cf. spec messika-pwa#3) — même logique que OrderDetail.tsx
   const isECF = order.type === type_order.retail_externe

   // Éditeur de remise ligne (ouvert sur clic de la cellule Remise)
   const [editingDiscountSku, setEditingDiscountSku] = useState<string | null>(null)
   // Éditeur de remise globale (toolbar)
   const [showBulkDiscountEditor, setShowBulkDiscountEditor] = useState(false)

   // Quota
   const [quotas, setQuotas] = useState<Map<string, SkuQuotaDetail>>(new Map())
   const [quotaLoading, setQuotaLoading] = useState(true)

   // Stock par ligne (clé = SKU de la ligne)
   const [stocks, setStocks] = useState<Map<string, StockResult>>(new Map())

   // Confirmations
   const [showConfirmValidate, setShowConfirmValidate] = useState(false)
   const [showConfirmCancel, setShowConfirmCancel] = useState(false)
   // Popup reliquat (identique au "Soumettre" du menu) : lignes refusées + cases à cocher.
   const [showReliquatConfirm, setShowReliquatConfirm] = useState(false)
   const [refusedLinesForPopup, setRefusedLinesForPopup] = useState<ReliquatPopupLine[]>([])
   const [checkedRefusedLines, setCheckedRefusedLines] = useState<boolean[]>([])

   // Refs des inputs qté pour navigation Tab/Enter
   const qtyInputRefs = useRef<(HTMLInputElement | null)[]>([])
   // Valeurs texte temporaires pour les champs qté (permet de vider sans valider à 0)
   const [editingQty, setEditingQty] = useState<Record<string, string>>({})
   // Ref du champ qté pour le passage de focus SKU → Qté dans "Ajouter ligne"
   const addLineQtyRef = useRef<HTMLInputElement | null>(null)

   // Tri des colonnes
   type SortKey = 'sku' | 'qty' | 'quota' | 'consumed' | 'remaining' | 'pu' | 'total'
   const [sortKey, setSortKey] = useState<SortKey | null>('sku')
   const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
   const toggleSort = (key: SortKey) => {
      if (sortKey === key) {
         setSortDir(prev => prev === 'asc' ? 'desc' : 'asc')
      } else {
         setSortKey(key)
         setSortDir('asc')
      }
   }

   // PU résolu : prix stocké sur la ligne, sinon min du tarif cession du client
   const resolvePU = useCallback((sku: string, linePrice?: number): number => {
      if (linePrice != null && linePrice > 0) return linePrice
      return getMinMaxCessPriceForModel(sku, region, currency)?.min ?? 0
   }, [getMinMaxCessPriceForModel, region, currency])

   // Lignes affichées (triées si un tri est actif, sinon ordre d'origine)
   const displayedLines = useMemo(() => {
      if (!sortKey) return draftLines
      const getValue = (dl: typeof draftLines[0]): string | number => {
         if (sortKey === 'sku') return dl.sku
         if (sortKey === 'qty') return dl.draftQty
         if (sortKey === 'pu') return resolvePU(dl.sku, dl.price)
         if (sortKey === 'total') return resolvePU(dl.sku, dl.price) * dl.draftQty
         const q = quotas.get(dl.sku)
         if (sortKey === 'quota') return q?.budgeted ?? -Infinity
         if (sortKey === 'consumed') return q?.budgeted != null ? q.consumed : -Infinity
         if (sortKey === 'remaining') return q?.remaining ?? -Infinity
         return 0
      }
      const factor = sortDir === 'asc' ? 1 : -1
      return [...draftLines].sort((a, b) => {
         const va = getValue(a)
         const vb = getValue(b)
         if (va < vb) return -1 * factor
         if (va > vb) return 1 * factor
         return 0
      })
   }, [draftLines, quotas, sortKey, sortDir, resolvePU])

   const renderSortIcon = (key: SortKey) => {
      if (sortKey !== key) return null
      return <FontAwesomeIcon icon={sortDir === 'asc' ? faCaretUp : faCaretDown} className={styles.sort_icon_active} />
   }

   // Trouver le prochain/précédent index non-supprimé pour la navigation clavier
   const findNextEditableIdx = (fromIdx: number, forward: boolean): number | null => {
      const step = forward ? 1 : -1
      let i = fromIdx + step
      while (i >= 0 && i < displayedLines.length) {
         if (!displayedLines[i].deleted) return i
         i += step
      }
      return null
   }

   const getQtyDisplay = (sku: string, draftQty: number) => {
      return editingQty[sku] ?? String(draftQty)
   }

   const commitQty = (sku: string, draftQty: number) => {
      const raw = editingQty[sku]
      if (raw !== undefined) {
         const parsed = parseInt(raw, 10)
         if (!isNaN(parsed) && parsed >= 0) {
            setLineQty(sku, parsed)
         }
         // Si vide ou invalide, on ne fait rien (la valeur reste à draftQty)
         setEditingQty(prev => { const next = { ...prev }; delete next[sku]; return next })
      }
   }
   const stickyHeaderRef = useRef<HTMLDivElement>(null)
   const containerRef = useRef<HTMLDivElement>(null)

   // Ajout de ligne
   const [showAddLine, setShowAddLine] = useState(false)
   const [addLineSku, setAddLineSku] = useState('')
   const [addLineQty, setAddLineQty] = useState<number | ''>(1)

   // Affichage de la colonne PCB (par combien) — persisté dans localStorage
   const PCB_STORAGE_KEY = 'rbv3.validation.showPCB'
   const [showPCB, setShowPCB] = useState<boolean>(() => {
      try { return localStorage.getItem(PCB_STORAGE_KEY) === 'true' } catch { return false }
   })
   useEffect(() => {
      try { localStorage.setItem(PCB_STORAGE_KEY, showPCB ? 'true' : 'false') } catch {}
   }, [showPCB])

   // Mesurer la hauteur du header sticky pour positionner le thead
   useEffect(() => {
      if (stickyHeaderRef.current && containerRef.current) {
         const h = stickyHeaderRef.current.offsetHeight
         containerRef.current.style.setProperty('--validation-header-height', `${h}px`)
      }
   })

   // Chargement quotas au mount
   useEffect(() => {
      let cancelled = false
      setQuotaLoading(true)
      getRetailQuota(order.customer_id, order.id).then(result => {
         if (!cancelled) {
            setQuotas(result.skuQuotas)
            setQuotaLoading(false)
         }
      })
      return () => { cancelled = true }
   }, [getRetailQuota, order.customer_id, order.id])

   // Garantit que `order.original_lines` existe avant la 1re modif. Sinon, après une
   // sortie/retour sur la page, `baseLines` retombe sur `order.lines` (déjà modifié par
   // l'autosave) et originalQty == draftQty → les lignes modifiées perdent leur surlignage.
   useEffect(() => {
      if (!isDraft && !order.original_lines) {
         updateOrderLocally(order.id as string, { original_lines: order.lines })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   // Même garantie que ci-dessus, pour l'adresse (messika-pwa#5) : capture le snapshot une
   // seule fois à l'ouverture de l'écran, pour pouvoir restaurer via "Annuler".
   useEffect(() => {
      if (!isDraft && !order.original_addresses) {
         updateOrderLocally(order.id as string, { original_addresses: order.addresses })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   // Autosave du brouillon : chaque modif (qty, add, delete, reset) est persistée immédiatement
   // dans `order.lines` via updateOrderLocally (write Dexie, pas de push AW — ce n'est qu'un draft).
   // Le push AW arrive au doValidate avec le changement de status. `order.original_lines` reste
   // intact → le bouton Annuler peut toujours restaurer l'état initial via reset().
   const isFirstDraftRender = useRef(true)
   useEffect(() => {
      if (isFirstDraftRender.current) {
         // 1er render : draftLines est encore identique à order.lines, pas besoin de persister
         isFirstDraftRender.current = false
         return
      }
      updateOrderLocally(order.id as string, { lines: buildFinalLines() })
   }, [draftLines, buildFinalLines, order.id, updateOrderLocally])

   // Sélection des dépôts à interroger pour le stock retail : cf. getRetailStockDepots
   // (src/libs/retailStockDepots.ts) pour la règle par rôle.
   const stockDepots = useMemo<string[]>(
      () => getRetailStockDepots(roleForCustomer, customer?.depot_retail ?? null),
      [roleForCustomer, customer?.depot_retail],
   )
   // Clé stable pour les deps de l'effet (évite les re-fetch par identité de tableau)
   const stockDepotsKey = stockDepots.join('|')

   // Préchargement des stocks pour chaque SKU présent dans la commande (au mount + ajout de lignes)
   // Dédupliqué par model_id car `getStock` interroge un doc { depot, model } qui couvre toutes les variations.
   useEffect(() => {
      let cancelled = false
      const toFetch: Array<{ sku: string, modelId: string }> = []
      for (const dl of draftLines) {
         if (stocks.has(dl.sku)) continue
         const modelId = getRetailModelById(dl.sku)?.id ?? dl.sku.split('-')[0]
         if (modelId) toFetch.push({ sku: dl.sku, modelId })
      }
      if (toFetch.length === 0) return
      // Chaque fetch est isolé par .catch() : l'échec d'une ligne ne doit jamais empêcher
      // l'affichage du stock des autres lignes ni provoquer de rejet non intercepté.
      Promise.all(toFetch.map(async ({ sku, modelId }) => {
         try {
            return { sku, result: await fetchStock(modelId, stockDepots) }
         } catch {
            return null
         }
      }))
         .then(entries => {
            if (cancelled) return
            setStocks(prev => {
               const next = new Map(prev)
               for (const entry of entries) {
                  if (entry) next.set(entry.sku, entry.result)
               }
               return next
            })
         })
      return () => { cancelled = true }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [draftLines, stockDepotsKey, fetchStock, getRetailModelById, stocks])

   // Résumé pour la popup de validation
   const summary = useMemo(() => {
      let accepted = 0, deleted = 0, added = 0, modified = 0
      for (const dl of draftLines) {
         if (dl.deleted) { deleted++; continue }
         if (dl.isNew) { added++ }
         else if (dl.draftQty !== dl.originalQty) { modified++ }
         accepted++
      }
      return { accepted, deleted, added, modified }
   }, [draftLines])

   // Résumé lisible pour la popup : catégories DISJOINTES (somme = nb de lignes), pas de
   // double comptage. "inchangée/modifiée/ajoutée" sont mutuellement exclusives ; "modifiée"
   // = qté différente de la demande INITIALE DU CLIENT (donc inclut une éventuelle modif d'un
   // valideur précédent, ex. N2), d'où la ligne de précision explicite.
   const summaryLine = useMemo(() => {
      const unchanged = summary.accepted - summary.modified - summary.added
      const parts: string[] = []
      if (unchanged > 0) parts.push(`${unchanged} ${trans('?inchangée', unchanged)}`)
      if (summary.modified > 0) parts.push(`${summary.modified} ${trans('?modifiée', summary.modified)}`)
      if (summary.added > 0) parts.push(`${summary.added} ${trans('?ajoutée', summary.added)}`)
      if (summary.deleted > 0) parts.push(`${summary.deleted} ${trans('?supprimée', summary.deleted)}`)
      const line = parts.length ? parts.join(', ') : trans('?aucune ligne conservée')
      return summary.modified > 0 ? `${line}\n${trans('?vs_demande_client')}` : line
   }, [summary, trans])

   // Clic sur le bouton vert : détecte un éventuel reliquat (lignes dont la qté finale < demande
   // initiale du client) ET affiche, le cas échéant, la MÊME popup que le "Soumettre" du menu
   // (lignes refusées + cases à cocher). Sinon, popup de confirmation résumée habituelle.
   const onClickValidate = useCallback(() => {
      const finalLines = buildFinalLines()
      const orig = order.original_lines ?? []
      // Reliquat proposé/créé UNIQUEMENT si cette validation envoie la commande en 'locked'
      // (étape finale → M3) ; sinon (validation intermédiaire N2 → wait_n1) : aucun reliquat.
      const refused = isLastValidationStep ? computeRefusedLines(orig, finalLines) : []
      const finalMap = new Map(finalLines.map(l => [l.sku, l.qty]))
      const origMap = new Map(orig.map(l => [l.sku, l.qty]))
      // Toujours réinitialiser l'état (vide si aucun refus) → pas de reliquat fantôme issu d'un
      // affichage précédent si le valideur annule puis revalide sans coupe.
      setRefusedLinesForPopup(refused.map(r => ({
         sku: r.sku,
         label: getRetailModelById(r.sku)?.label ?? '',
         origQty: origMap.get(r.sku) ?? 0,
         curQty: finalMap.get(r.sku) ?? 0,
         reliquat: r.qty,
      })))
      setCheckedRefusedLines(refused.map(() => true))
      if (refused.length > 0) {
         setShowReliquatConfirm(true)   // même popup que le "Soumettre" du menu
      } else {
         setShowConfirmValidate(true)   // pas de reliquat → confirmation résumée habituelle
      }
   }, [buildFinalLines, order.original_lines, computeRefusedLines, getRetailModelById, isLastValidationStep])

   // Validation — le statut cible dépend du rôle effectif et de la présence d'un N1 derrière (cf. WORKFLOW_BATCH).
   const doValidate = useCallback(async () => {
      const finalLines = buildFinalLines()
      if (finalLines.length === 0) {
         await deleteOrder(order.id as string)
      } else {
         // Écrit les lignes finales (édits du valideur) en local, puis transition via webhook :
         // le serveur décide le statut cible + pose les perms. Routage par statut courant.
         await updateOrderLocally(order.id as string, { lines: finalLines })
         // Lignes refusées cochées à reporter en reliquat : transmises au webhook, qui crée le
         // reliquat côté serveur (perms client+admins) UNIQUEMENT si la validation produit 'locked'.
         const reliquatLines = refusedLinesForPopup
            .filter((_, i) => checkedRefusedLines[i])
            .map(r => ({ sku: r.sku, qty: r.reliquat }))
         const result = order.status === order_status.wait_n2
            ? await validateRetailN2(order, reliquatLines)   // → wait_n1 (si N1) ou locked (+ reliquat si locked)
            : await validateRetailN1(order, reliquatLines)   // → locked (+ reliquat)
         if (!result) {
            // Échec (droits/webhook/AW) : un toast d'erreur a été émis. On RESTE sur la commande
            // (pas de navigation) — sinon le ToastContainer d'OrderDetail se démonte et le toast
            // disparaît aussitôt. L'utilisateur voit l'erreur et peut réessayer.
            setShowConfirmValidate(false)
            setShowReliquatConfirm(false)
            return
         }
      }
      setShowConfirmValidate(false)
      setShowReliquatConfirm(false)
      // Quitte la commande : plus sélectionnée dans la searchbar, le parent (OrderDetail) navigue vers la liste
      setCurOrder(null)
      onValidated()
   }, [buildFinalLines, deleteOrder, onValidated, order, order_status, setCurOrder, updateOrderLocally, validateRetailN2, validateRetailN1, refusedLinesForPopup, checkedRefusedLines])

   // Brouillon : « Quitter commande » — sauvegarde comme un brouillon new_mgr (local + push AW), statut inchangé
   const doQuitDraft = useCallback(async () => {
      await updateOrderLocally(order.id as string, { lines: buildFinalLines() })
      awPushOrder?.(order.id as string)
      onClose()
   }, [updateOrderLocally, order.id, buildFinalLines, awPushOrder, onClose])

   // Annulation
   const doCancel = useCallback(() => {
      reset()
      if (isAddressDirty) {
         updateOrderLocally(order.id as string, { addresses: baseAddresses })
      }
      setShowConfirmCancel(false)
   }, [reset, isAddressDirty, order.id, baseAddresses, updateOrderLocally])

   // Ajout de ligne — supporte multi-SKU via virgule : "E-0001*6,E-0012*15"
   // Retourne true si au moins une ligne a été ajoutée. `defaultQty` : quantité d'un SKU seul sans *qty.
   const addFromText = useCallback((text: string, defaultQty: number): boolean => {
      const { lines: parsed, badTokens } = parseSkuQtyInput(text, defaultQty)
      if (badTokens.length > 0) {
         toast.error(trans('?Quantité invalide') + ' : ' + badTokens.join(', '), {
            position: 'top-right', autoClose: 3500, theme: 'colored',
         })
         return false
      }

      // Ajout de chaque ligne (useValidationDraft cumule déjà les doublons)
      // Remise stockée par famille selon la grille (DocRetail/GRILLE_ECR_ECF.txt) :
      //  - ECR/ECF : getStoredDiscount (mappe NF→0, 100→100, 0→0)
      //  - KOT/KTM : prix sans intérêt sur les quotas → discount=0 par défaut
      const isECRorECF = order.type === type_order.retail_interne || order.type === type_order.retail_externe
      let added = 0
      for (const line of parsed) {
         const model = getRetailModelById(line.sku)
         if (!model) continue
         let finalLine: OrderLine = line
         if (isECRorECF) {
            const stored = getStoredDiscount(model.fam, customer)
            if (stored !== null) finalLine = { ...line, discount: stored }
         } else {
            finalLine = { ...line, discount: 0 }
         }
         addLine(finalLine)
         added++
      }
      return added > 0
   }, [addLine, customer, getRetailModelById, order.type, trans, type_order.retail_externe, type_order.retail_interne])

   const doAddLine = useCallback(() => {
      if (!addLineSku.trim()) return
      if (addFromText(addLineSku, typeof addLineQty === 'number' ? addLineQty : 1)) {
         setAddLineSku('')
         setAddLineQty(1)
         setShowAddLine(false)
      }
   }, [addFromText, addLineSku, addLineQty])

   // Exposé à OrderDetail (barre du haut, mode crayon) : même logique que « Ajouter ligne »
   useImperativeHandle(ref, () => ({
      addFromInput: (text: string) => addFromText(text, 1),
   }), [addFromText])

   // Rendu d'une ligne
   function renderLine(dl: typeof draftLines[0], idx: number) {
      const model = getRetailModelById(dl.sku)
      const quota = quotas.get(dl.sku)
      const budgeted = quota?.budgeted ?? null
      const consumed = quota?.consumed ?? 0
      const remaining = getSkuRemaining(quotas, dl.sku)
      const overQuota = budgeted !== null && (consumed + dl.draftQty > budgeted)

      // Prix unitaire (PU) : prix stocké sur la ligne, sinon min du tarif cession du client
      const pu = resolvePU(dl.sku, dl.price)
      const lineTotal = pu * dl.draftQty

      // Stock : préchargé, null pendant le fetch
      const stock = stocks.get(dl.sku) ?? null

      // Brouillon : pas de surlignage « modifiée / ajoutée vs demande client »
      const qtyChanged = !isDraft && dl.draftQty !== dl.originalQty && !dl.isNew
      const showNew = !isDraft && dl.isNew
      const rowClass = [
         styles.row,
         dl.deleted ? styles.row_deleted : qtyChanged ? styles.row_modified : styles.row_ok,
         showNew ? styles.row_new : '',
      ].filter(Boolean).join(' ')

      return (
         <tr key={dl.sku} className={rowClass}>
            {/* SKU */}
            <td className={styles.col_sku}>
               {dl.sku}
               {showNew && <span className={styles.badge_new}>{trans('?NEW')}</span>}
            </td>
            {/* Label */}
            <td className={styles.col_label}>
               {isExcluBoutique(model) && <FontAwesomeIcon icon={faStars} className={styles.exclu_boutique} />}
               {model?.label ?? ''}
            </td>
            {/* Stock : détail par dépôt (idem picker) ; en multi-dépôt, le dépôt du client est en gras */}
            <td className={styles.col_stock}>
               {stock === null ? (
                  <span className={styles.stock_loading}>…</span>
               ) : (() => {
                  const entries = Object.entries(stock.per_depot)
                  if (entries.length === 0) return '—'   // aucun dépôt interrogeable → stock inconnu
                  const isMulti = entries.length > 1
                  const custDepot = customer?.depot_retail ?? null
                  return (
                     <span className={stock.available ? styles.stock_ok : styles.stock_ko}>
                        {entries.map(([code, qty], i) => {
                           const qtyDisplay = typeof qty === 'number' ? formatNumber(qty, 0) : String(qty)
                           const codeLabel = code.startsWith('S') ? code.substring(1) : code
                           const isCust = isMulti && code === custDepot
                           const labelEl = <>{codeLabel}:&nbsp;{qtyDisplay}</>
                           return (
                              <span key={code}>
                                 {i > 0 && ', '}
                                 {isCust ? <span className={styles.stock_customer_depot}>{labelEl}</span> : labelEl}
                              </span>
                           )
                        })}
                     </span>
                  )
               })()}
            </td>
            {/* PCB (par combien) — réservé admin/N1.
                Le PCB du dépôt du client est mis en évidence par un badge :
                  - vert si la qté demandée est compatible (multiple du PCB)
                  - rouge si non compatible. */}
            {canShowPCB && showPCB && (() => {
               const isPcbModel = model?.fam === glob_const.retail_fam_pcb
               const pcbField = customer?.depot_retail ? glob_const.retail_depot_pcb_field?.[customer.depot_retail] : null
               const pcbForDepot = pcbField ? Number(model?.[pcbField] ?? 0) : 0
               const compatible = pcbForDepot > 0 && dl.draftQty > 0 && (dl.draftQty % pcbForDepot === 0)
               const custDepot = customer?.depot_retail ?? null
               const badgeClass = compatible ? styles.pcb_badge_ok : styles.pcb_badge_ko
               // Mapping label affiché → champ du model + dépôts associés (SMO retombe sur FR comme retail_depot_pcb_field)
               const pcbEntries: Array<{ label: string, value: number | null | undefined, depots: string[] }> = [
                  { label: 'FR', value: model?.pcb_fr, depots: ['SFR', 'SMO'] },
                  { label: 'US', value: model?.pcb_us, depots: ['SUS'] },
                  { label: 'CN', value: model?.pcb_cn, depots: ['SCN'] },
                  { label: 'JP', value: model?.pcb_jp, depots: ['SJP'] },
               ]
               return (
                  <td className={styles.col_pcb}>
                     {!isPcbModel ? '—' : pcbEntries.map((e, i) => {
                        const valDisplay = formatNumberWithNC(e.value ?? null)
                        const isCust = !!custDepot && e.depots.includes(custDepot)
                        const textEl = <>{e.label}&nbsp;:&nbsp;{valDisplay}</>
                        return (
                           <span key={e.label}>
                              {i > 0 && ', '}
                              {isCust ? <span className={badgeClass}>{textEl}</span> : textEl}
                           </span>
                        )
                     })}
                  </td>
               )
            })()}
            {/* Qté quota */}
            <td className={`${styles.col_num} ${styles.col_quota_first}`}>{budgeted !== null ? formatNumber(budgeted, 0) : '—'}</td>
            {/* Qté consommée */}
            <td className={`${styles.col_num} ${styles.col_quota_mid}`}>{budgeted !== null ? formatNumber(consumed, 0) : '—'}</td>
            {/* Quota restant */}
            <td className={`${styles.col_num} ${styles.col_quota_last}`}>{remaining !== null ? formatNumber(remaining, 0) : '—'}</td>
            {/* PU — "Gratuit" si grille=NF pour ce client (100 affiche le prix + %age en col séparée) */}
            <td className={styles.col_price}>{pu > 0 ? formatRetailPrice(model, formatCESPrice(pu, currency), customer, 'order_line') : '-'}</td>
            {/* Total ligne — idem */}
            <td className={styles.col_price}>{pu > 0 && dl.draftQty > 0 ? formatRetailPrice(model, formatCESPrice(lineTotal, currency), customer, 'order_line') : '-'}</td>
            {/* Remise ligne (ECF uniquement) — pas d'éditeur sur une ligne supprimée */}
            {isECF && (
               <td
                  className={styles.col_discount}
                  style={{ cursor: !dl.deleted ? 'pointer' : 'default' }}
                  onClick={() => { if (!dl.deleted) setEditingDiscountSku(dl.sku) }}
               >
                  {`${dl.discount ?? 0}%`}
               </td>
            )}
            {/* Qté courante (éditable) */}
            <td className={`${styles.col_qty} ${qtyChanged ? styles.qty_changed : ''}`}>
               {!dl.deleted ? (
                  <div className={styles.qty_editor}>
                     <button onClick={() => setLineQty(dl.sku, Math.max(0, dl.draftQty - 1))} disabled={dl.draftQty <= 0}>
                        -
                     </button>
                     <input
                        ref={el => { qtyInputRefs.current[idx] = el }}
                        type="text"
                        inputMode="numeric"
                        className={styles.qty_input}
                        value={getQtyDisplay(dl.sku, dl.draftQty)}
                        onFocus={e => e.target.select()}
                        onChange={e => {
                           const val = e.target.value
                           if (val === '' || /^\d+$/.test(val)) {
                              setEditingQty(prev => ({ ...prev, [dl.sku]: val }))
                           }
                        }}
                        onBlur={() => commitQty(dl.sku, dl.draftQty)}
                        onKeyDown={e => {
                           if (e.key === 'Enter') {
                              e.preventDefault()
                              commitQty(dl.sku, dl.draftQty)
                              const nextIdx = findNextEditableIdx(idx, true)
                              if (nextIdx !== null) {
                                 setTimeout(() => {
                                    qtyInputRefs.current[nextIdx]?.scrollIntoView({ block: 'nearest' })
                                    qtyInputRefs.current[nextIdx]?.focus()
                                    qtyInputRefs.current[nextIdx]?.select()
                                 })
                              }
                           } else if (e.key === 'Tab') {
                              e.preventDefault()
                              commitQty(dl.sku, dl.draftQty)
                              const nextIdx = findNextEditableIdx(idx, !e.shiftKey)
                              if (nextIdx !== null) {
                                 setTimeout(() => {
                                    qtyInputRefs.current[nextIdx]?.scrollIntoView({ block: 'nearest' })
                                    qtyInputRefs.current[nextIdx]?.focus()
                                    qtyInputRefs.current[nextIdx]?.select()
                                 })
                              }
                           }
                        }}
                     />
                     <button onClick={() => setLineQty(dl.sku, dl.draftQty + 1)}>
                        +
                     </button>
                  </div>
               ) : (
                  <span className={styles.qty_deleted}>{dl.originalQty}</span>
               )}
            </td>
            {/* Alerte dépassement */}
            <td className={styles.col_alert}>
               {overQuota && !dl.deleted && (
                  <FontAwesomeIcon icon={faTriangleExclamation} className={styles.alert_icon} />
               )}
            </td>
            {/* Actions */}
            <td className={styles.col_actions}>
               {!dl.deleted ? (
                  <button className={styles.action_btn} onClick={() => deleteLine(dl.sku)} title={trans('?Supprimer')}>
                     <FontAwesomeIcon icon={faTrash} />
                  </button>
               ) : (
                  <button className={styles.action_btn} onClick={() => undeleteLine(dl.sku)} title={trans('?Annuler suppression')}>
                     <FontAwesomeIcon icon={faArrowRotateLeft} />
                  </button>
               )}
            </td>
         </tr>
      )
   }

   return (
      <div className={styles.container} ref={containerRef}>
         {/* Header sticky : toolbar + ajout ligne */}
         <div className={styles.sticky_header} ref={stickyHeaderRef}>
            <div className={styles.toolbar}>
               <div className={styles.toolbar_left}>
                  <Button small secondary lefticon={<FontAwesomeIcon icon={faArrowLeft} />} onClick={() => {
                     // L'autosave a déjà persisté les modifs → on peut quitter librement.
                     // Un éventuel timer en attente est annulé par le cleanup du useEffect au démontage.
                     if (isDraft) doQuitDraft()
                     else onClose()
                  }}>
                     {trans(isDraft ? '?Quitter commande' : '?Quitter validation')}
                  </Button>
                  {isDraft && (
                     <Button small secondary onClick={onReturnToClient}>
                        {trans('?Envoyer au client')}
                     </Button>
                  )}
                  <Button small green lefticon={<FontAwesomeIcon icon={faCheck} />} onClick={isDraft ? onFinalValidation : onClickValidate} disabled={isDraft && !isOrderComplete(order)}>
                     {/* Dernier maillon = admin (shortcut), N1, ou N2 sans N1 derrière → "Sync M3" ; sinon "Soumettre" */}
                     {trans(isDraft ? '?Validation finale' : isLastValidationStep ? '?Synchroniser vers M3' : '?Soumettre')}
                  </Button>
                  {!isDraft && (
                     <Button small red={isDirty} secondary={!isDirty} lefticon={<FontAwesomeIcon icon={faXmark} />} onClick={() => isDirty ? setShowConfirmCancel(true) : null} disabled={!isDirty}>
                        {trans('?Annuler')}
                     </Button>
                  )}
                  {/* Résumé */}
                  <div className={styles.summary}>
                     <span className={styles.summary_total}>{draftLines.length} {trans('?ligne', draftLines.length)}</span>
                     {!isDraft && <span className={styles.summary_ok}>{summary.accepted} {trans('?acceptée', summary.accepted)}</span>}
                     {!isDraft && <span className={styles.summary_deleted}>{summary.deleted} {trans('?supprimée', summary.deleted)}</span>}
                     {!isDraft && summary.modified > 0 && <span className={styles.summary_modified}>{summary.modified} {trans('?modifiée', summary.modified)}</span>}
                     {!isDraft && summary.added > 0 && <span className={styles.summary_added}>{summary.added} {trans('?ajoutée', summary.added)}</span>}
                  </div>
               </div>
               <div className={styles.toolbar_right}>
                  {canShowPCB && (
                     <div style={{ marginRight: '30px' }}>
                        <Checkbox
                           small
                           checked={showPCB ? CheckboxStates.Checked : CheckboxStates.Unchecked}
                           onChange={(newState) => setShowPCB(newState === CheckboxStates.Checked)}
                           caption={trans('?Afficher le PCB')}
                        />
                     </div>
                  )}
                  {isECF && (
                     <Button small secondary lefticon={<FontAwesomeIcon icon={faPercent} />} onClick={() => setShowBulkDiscountEditor(true)}>
                        {trans('?Remise globale')}
                     </Button>
                  )}
                  {!isDraft && (
                     <Button small secondary red lefticon={<FontAwesomeIcon icon={faBan} />} onClick={() => draftLines.forEach(dl => { if (!dl.deleted) deleteLine(dl.sku) })}>
                        {trans('?Tout refuser')}
                     </Button>
                  )}
                  <Button small secondary lefticon={<FontAwesomeIcon icon={faPlus} />} onClick={() => setShowAddLine(true)}>
                     {trans('?Ajouter ligne')}
                  </Button>
               </div>
            </div>

            {showAddLine && (
               <div className={styles.add_line}>
                  <input
                     type="text"
                     placeholder={trans('?Référence SKU')}
                     value={addLineSku}
                     onChange={e => setAddLineSku(e.target.value.toUpperCase())}
                     onKeyDown={e => {
                        if (e.key === 'Enter') {
                           e.preventDefault()
                           addLineQtyRef.current?.focus()
                           addLineQtyRef.current?.select()
                        } else if (e.key === 'Escape') {
                           e.preventDefault()
                           setShowAddLine(false)
                        }
                     }}
                     autoFocus
                  />
                  <input
                     ref={addLineQtyRef}
                     type="text"
                     inputMode="numeric"
                     className={styles.add_line_qty}
                     value={addLineQty}
                     onChange={e => {
                        const digits = e.target.value.replace(/\D/g, '')
                        if (digits === '') {
                           setAddLineQty('')
                        } else {
                           const n = Number(digits)
                           setAddLineQty(n > 0 ? n : 1)
                        }
                     }}
                     onBlur={() => {
                        if (typeof addLineQty !== 'number' || addLineQty < 1) setAddLineQty(1)
                     }}
                     onKeyDown={e => {
                        if (e.key === 'Enter') {
                           e.preventDefault()
                           doAddLine()
                        } else if (e.key === 'Escape') {
                           e.preventDefault()
                           setShowAddLine(false)
                        }
                     }}
                  />
                  <Button small green lefticon={<FontAwesomeIcon icon={faCheck} />} onClick={doAddLine}>
                     {trans('?Ajouter')}
                  </Button>
                  <Button small secondary lefticon={<FontAwesomeIcon icon={faXmark} />} onClick={() => setShowAddLine(false)}>
                     {trans('?Annuler')}
                  </Button>
               </div>
            )}
         </div>

         {/* Table */}
         {quotaLoading ? (
            <div className={styles.loading}>
               <FontAwesomeIcon icon={faLoader} spin /> {trans('?Chargement')}...
            </div>
         ) : (
            <>
               <table className={styles.table}>
                  <thead>
                     <tr>
                        <th className={`${styles.col_sku} ${styles.sortable}`} onClick={() => toggleSort('sku')}>
                           <span className={styles.sort_header}>{trans('?SKU')}{renderSortIcon('sku')}</span>
                        </th>
                        <th className={styles.col_label}>{trans('?Libellé')}</th>
                        <th className={styles.col_stock}>{trans('?Stock')}</th>
                        {canShowPCB && showPCB && <th className={styles.col_pcb}>{trans('?Par combien')}</th>}
                        <th className={`${styles.col_num} ${styles.sortable} ${styles.col_quota_first}`} onClick={() => toggleSort('quota')}>
                           <span className={styles.sort_header}>{trans('?Quota')}{renderSortIcon('quota')}</span>
                        </th>
                        <th className={`${styles.col_num} ${styles.sortable} ${styles.col_quota_mid}`} onClick={() => toggleSort('consumed')}>
                           <span className={styles.sort_header}>
                              {trans('?Cons.')}
                              <span className={styles.tip} data-tip={trans('?(hors cette cde)')} onClick={(e) => e.stopPropagation()}>
                                 <FontAwesomeIcon
                                    icon={faCircleQuestion}
                                    style={{ marginLeft: 4, opacity: 0.6, cursor: 'help' }}
                                 />
                              </span>
                              {renderSortIcon('consumed')}
                           </span>
                        </th>
                        <th className={`${styles.col_num} ${styles.sortable} ${styles.col_quota_last}`} onClick={() => toggleSort('remaining')}>
                           <span className={styles.sort_header}>{trans('?Rest.')}{renderSortIcon('remaining')}</span>
                        </th>
                        <th className={`${styles.col_price} ${styles.sortable}`} onClick={() => toggleSort('pu')}>
                           <span className={styles.sort_header}>{trans('?PU')}{renderSortIcon('pu')}</span>
                        </th>
                        <th className={`${styles.col_price} ${styles.sortable}`} onClick={() => toggleSort('total')}>
                           <span className={styles.sort_header}>{trans('?Total')}{renderSortIcon('total')}</span>
                        </th>
                        {isECF && <th className={styles.col_discount}>{trans('?Remise')}</th>}
                        <th className={`${styles.col_qty} ${styles.sortable}`} onClick={() => toggleSort('qty')}>
                           <span className={styles.sort_header}>{trans('?Qté')}{renderSortIcon('qty')}</span>
                        </th>
                        <th className={styles.col_alert}></th>
                        <th className={styles.col_actions}></th>
                     </tr>
                  </thead>
                  <tbody>
                     {displayedLines.map((dl, i) => renderLine(dl, i))}
                  </tbody>
               </table>
            </>
         )}

         {/* Popup de confirmation de validation — titre/texte selon rôle effectif et présence d'un N1 */}
         {showConfirmValidate && (
            <Confirm
               title={trans(isLastValidationStep
                  ? '?Validation finale et envoi M3'
                  : effectiveRetailRole === 'N2'
                     ? '?Soumettre au valideur final'
                     : '?Soumettre la commande pour validation')}
               text={`${summaryLine}\n\n${trans(isLastValidationStep
                  ? '?msg_validation_finale'
                  : effectiveRetailRole === 'N2'
                     ? '?msg_soumettre_n1'
                     : '?msg_soumettre_validation')}`}
               onOK={doValidate}
               onCancel={() => setShowConfirmValidate(false)}
               destructive={summary.accepted === 0}
            />
         )}

         {/* Popup reliquat — identique au "Soumettre" du menu (Orders / OrderDetail). Affichée quand
             le valideur a réduit/supprimé des quantités p/r à la demande client → propose le report. */}
         {showReliquatConfirm && (
            <ReliquatConfirm
               title={trans(isLastValidationStep ? '?Validation finale' : '?Soumettre')}
               lines={refusedLinesForPopup}
               checked={checkedRefusedLines}
               onToggle={(i) => setCheckedRefusedLines(prev => prev.map((v, j) => j === i ? !v : v))}
               onOK={doValidate}
               onCancel={() => setShowReliquatConfirm(false)}
            />
         )}

         {/* Éditeur de remise ligne (ECF) */}
         {editingDiscountSku !== null && (
            <DiscountEditor
               title={trans('?Remise ligne')}
               icon={<FontAwesomeIcon icon={faPercent} />}
               value={draftLines.find(dl => dl.sku === editingDiscountSku)?.discount ?? 0}
               onCancel={() => setEditingDiscountSku(null)}
               onValidate={(val) => {
                  setLineDiscount(editingDiscountSku, val)
                  setEditingDiscountSku(null)
               }}
               top={'20%'}
               minWidth={'25%'}
               maxWidth={'40%'}
            />
         )}

         {/* Éditeur de remise globale (ECF) — applique à toutes les lignes non supprimées */}
         {showBulkDiscountEditor && (
            <DiscountEditor
               title={trans('?Remise globale')}
               icon={<FontAwesomeIcon icon={faPercent} />}
               value={0}
               onCancel={() => setShowBulkDiscountEditor(false)}
               onValidate={(val) => {
                  // Les lignes gratuites de la grille (100 %) restent à 100 %
                  draftLines.forEach(dl => {
                     if (dl.deleted) return
                     if (getStoredDiscount(getRetailModelById(dl.sku)?.fam, customer) === 100) return
                     setLineDiscount(dl.sku, val)
                  })
                  setShowBulkDiscountEditor(false)
               }}
               top={'20%'}
               minWidth={'25%'}
               maxWidth={'40%'}
            />
         )}

         {/* Popup de confirmation d'annulation */}
         {showConfirmCancel && (
            <Confirm
               title={trans('?Annuler les modifications')}
               text={trans('?Toutes les modifications seront perdues.')}
               onOK={doCancel}
               onCancel={() => setShowConfirmCancel(false)}
               yesno
            />
         )}

      </div>
   )
})

export default RetailValidationTable
